import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import apiService from "../../services/api";
import ConfirmarConcessaoDetalhe from "./ConfirmarConcessaoDetalhe";
import { usePopup } from "../common/Popup";
import visibilityIcon from "../../assets/visibility.png";
import "../features/users/user-table.css";

const ContentArea = styled.div`
  padding: 0px 0 20px;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnitTableWrapper = styled.div`
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 18px;
`;

const UnitTitle = styled.div`
  padding: 14px 32px;
  background: #f2f0f0;
  font-size: 16px;
  color: #6ab8a8;
  font-weight: 600;
`;

const UnitTitleValue = styled.span`
  color: #555555;
  padding-left: 8px;
`;

const UnitSeparator = styled.div`
  height: 1px;
  background: #dfe3e8;
`;

const StackedHeaderCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.15;
  font-size: 14px;
  text-align: center;
`;

const GRID_COLUMNS = "1fr 1fr 0.7fr 1fr 1.2fr 0.85fr 1.3fr";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding-left: 20px;
  height: 50px;
  background: #f2f0f0;
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const UnitRowsWrapper = styled.div``;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding-left: 20px;
  height: 58px;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;

    &:last-child {
      border-right: none;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;

  .btn-approve,
  .btn-reject {
    justify-content: center;
  }
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

const formatTipoAtividade = (tipo) => {
  const tipos = {
    ESTAGIO_OBRIGATORIO: "Estágio Obrigatório",
    ESTAGIO_SUPERVISIONADO: "Estágio Supervisionado",
    INTERNATO_MEDICO: "Internato Médico",
    AULA_PRATICA: "Aula Prática",
  };
  return tipos[tipo] || tipo || "-";
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR");
};

const formatPeriodo = (periodos = []) => {
  if (!periodos || periodos.length === 0) return "-";
  const normalized = [...periodos].sort(
    (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
  );
  return normalized
    .map((periodo) => {
      const inicio = formatDate(periodo.dataInicio);
      const fim = formatDate(periodo.dataFim);
      if (!inicio && !fim) return "";
      if (!inicio) return fim;
      if (!fim) return inicio;
      return `${inicio} a ${fim}`;
    })
    .filter(Boolean)
    .join(" | ");
};

const ConfirmarConcessaoContent = ({
  solicitacaoIdFiltro = null,
  vagaIdFiltro = null,
  onlyConfirmada = false,
}) => {
  const popup = usePopup();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [viewConfirmada, setViewConfirmada] = useState(false);
  const [cancelandoId, setCancelandoId] = useState(null);
  const initialSelectionConsumed = useRef(false);

  const fetchSolicitacoes = async () => {
    setLoading(true);
    setError(null);
    try {
      if (onlyConfirmada) {
        const confirmadasResult = await apiService.getSolicitacoesVaga("CONFIRMADA");
        const confirmadas = confirmadasResult?.data
          ? confirmadasResult.data
          : confirmadasResult;
        setSolicitacoes(Array.isArray(confirmadas) ? confirmadas : []);
        return;
      }

      const [concedidasResult, confirmadasResult] = await Promise.all([
        apiService.getSolicitacoesVaga("CONCEDIDO"),
        apiService.getSolicitacoesVaga("CONFIRMADA"),
      ]);
      const concedidas = concedidasResult?.data
        ? concedidasResult.data
        : concedidasResult;
      const confirmadas = confirmadasResult?.data
        ? confirmadasResult.data
        : confirmadasResult;
      setSolicitacoes([
        ...(Array.isArray(concedidas) ? concedidas : []),
        ...(Array.isArray(confirmadas) ? confirmadas : []),
      ]);
    } catch (err) {
      setError(err.message || "Falha ao carregar concessões.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitacoes();
  }, [onlyConfirmada]);

  useEffect(() => {
    if (
      initialSelectionConsumed.current ||
      solicitacaoIdFiltro === null ||
      solicitacaoIdFiltro === undefined
    ) {
      return;
    }

    const solicitacao = solicitacoes.find(
      (item) => Number(item.id) === Number(solicitacaoIdFiltro)
    );
    if (solicitacao) {
      setViewConfirmada(true);
      setSelectedSolicitacao(solicitacao);
      initialSelectionConsumed.current = true;
    }
  }, [solicitacoes, solicitacaoIdFiltro]);

  const filteredSolicitacoes = useMemo(() => {
    if (vagaIdFiltro === null || vagaIdFiltro === undefined) return solicitacoes;
    return solicitacoes.filter((s) => {
      const id = s.vaga?.id ?? s.vagaId;
      return Number(id) === Number(vagaIdFiltro);
    });
  }, [solicitacoes, vagaIdFiltro]);

  const groupedByEstabelecimento = useMemo(() => {
    return filteredSolicitacoes.reduce((acc, solicitacao) => {
      const estabelecimentoNome =
        solicitacao.vaga?.estabelecimentoSaude?.nome || "-";
      if (!acc[estabelecimentoNome]) acc[estabelecimentoNome] = [];
      acc[estabelecimentoNome].push(solicitacao);
      return acc;
    }, {});
  }, [filteredSolicitacoes]);

  const handleConfirmar = (solicitacao) => {
    setViewConfirmada(false);
    setSelectedSolicitacao(solicitacao);
  };

  const handleVisualizar = (solicitacao) => {
    setViewConfirmada(true);
    setSelectedSolicitacao(solicitacao);
  };

  const handleVoltar = () => {
    setSelectedSolicitacao(null);
    setViewConfirmada(false);
    fetchSolicitacoes();
  };

  const handleCancelar = async (solicitacao) => {
    const confirmed = await popup.confirm({
      icon: "delete",
      title: "Cancelar concessão",
      message:
        "Deseja cancelar esta concessão? As vagas concedidas voltarão a ficar disponíveis.",
      confirmText: "Cancelar concessão",
      cancelText: "Voltar",
    });
    if (!confirmed) return;

    setCancelandoId(solicitacao.id);
    try {
      await apiService.cancelarConcessaoSolicitacaoVaga(solicitacao.id);
      setSolicitacoes((atuais) =>
        atuais.filter((item) => item.id !== solicitacao.id)
      );
      await popup.alert({
        icon: "success",
        message: "Concessão cancelada com sucesso.",
      });
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao cancelar concessão",
        message: err.message || "Tente novamente.",
      });
    } finally {
      setCancelandoId(null);
    }
  };

  if (selectedSolicitacao) {
    return (
      <ConfirmarConcessaoDetalhe
        solicitacao={selectedSolicitacao}
        onVoltar={handleVoltar}
        modoConfirmada={viewConfirmada}
      />
    );
  }

  return (
    <ContentArea>
      {loading && <EmptyMessage>Carregando...</EmptyMessage>}
      {error && <EmptyMessage>{error}</EmptyMessage>}

      {!loading && !error && filteredSolicitacoes.length === 0 && (
        <EmptyMessage>Nenhuma vaga concedida ou confirmada.</EmptyMessage>
      )}

      {!loading && !error && filteredSolicitacoes.length > 0 && (
        <ListWrapper>
          {Object.keys(groupedByEstabelecimento).map((estabelecimentoNome) => (
            <UnitTableWrapper key={estabelecimentoNome}>
              <UnitTitle>
                Unidade de saúde:
                <UnitTitleValue>{estabelecimentoNome}</UnitTitleValue>
              </UnitTitle>
              <UnitSeparator />
              <HeaderRow>
                <div>ESPECIALIDADE</div>
                <div>SETOR</div>
                <div>TURNO</div>
                <div>TIPO</div>
                <div>PERÍODO</div>
                <div>
                  <StackedHeaderCell>
                    <span>VAGAS</span>
                    <span>CONCEDIDAS</span>
                  </StackedHeaderCell>
                </div>
                <div>CONFIRMAR</div>
              </HeaderRow>

              <UnitRowsWrapper>
                {groupedByEstabelecimento[estabelecimentoNome].map(
                  (solicitacao) => (
                    <ListRow key={solicitacao.id}>
                      <div>
                        {solicitacao.vaga?.unidadeAssistencial?.especialidade
                          ?.nome || "-"}
                      </div>
                      <div>
                        {solicitacao.vaga?.unidadeAssistencial?.nome || "-"}
                      </div>
                      <div>{solicitacao.vaga?.turno || "-"}</div>
                      <div>
                        {formatTipoAtividade(solicitacao.vaga?.tipoAtividade)}
                      </div>
                      <div>{formatPeriodo(solicitacao.periodos)}</div>
                      <div>
                        {solicitacao.quantidadeAlunosConcedidos ??
                          solicitacao.quantidadeAlunos ??
                          "-"}
                      </div>
                      <ActionsCell>
                        {solicitacao.status === "CONFIRMADA" ? (
                          <>
                            <button
                              type="button"
                              className="btn-approve"
                              style={{ marginLeft: 0 }}
                              onClick={() => handleVisualizar(solicitacao)}
                              title="Ver vaga confirmada"
                            >
                              Confirmada ✓
                            </button>
                            <button
                              type="button"
                              className="btn-view-icon"
                              onClick={() => handleVisualizar(solicitacao)}
                              title="Ver vaga confirmada"
                            >
                              <img
                                src={visibilityIcon}
                                alt="Ver"
                                style={{ width: 22, height: 22 }}
                              />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn-approve"
                              onClick={() => handleConfirmar(solicitacao)}
                              disabled={cancelandoId === solicitacao.id}
                            >
                              Confirmar ✓
                            </button>
                            <button
                              type="button"
                              className="btn-reject"
                              onClick={() => handleCancelar(solicitacao)}
                              disabled={cancelandoId === solicitacao.id}
                            >
                              {cancelandoId === solicitacao.id
                                ? "Cancelando..."
                                : "Cancelar ✕"}
                            </button>
                          </>
                        )}
                      </ActionsCell>
                    </ListRow>
                  )
                )}
              </UnitRowsWrapper>
            </UnitTableWrapper>
          ))}
        </ListWrapper>
      )}
    </ContentArea>
  );
};

export default ConfirmarConcessaoContent;
